// ============================================================================
// COA LEDGER SERVICE (CRUD + flags + module tags, entity-scoped)
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { coaLedgerEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
  CoaLedgerDto,
  CreateCoaLedgerRequest,
  UpdateCoaLedgerRequest,
  CoaLedgerQueryParams,
  LedgerNatureDto,
} from '../types';

class CoaLedgerService {
  async getAll(params?: CoaLedgerQueryParams): Promise<PaginatedResponse<CoaLedgerDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(coaLedgerEndpoints.list(), params)
      : coaLedgerEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<CoaLedgerDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /**
   * Ledger-nature reference list (enum_master ACC_LEDGER_NATURE) for dropdowns. The ledger form
   * gets only what can be picked; a list filter passes includeSystemSet to add the natures the
   * system sets itself (Control, Intercompany).
   */
  async getNatures(includeSystemSet = false): Promise<LedgerNatureDto[]> {
    const url = includeSystemSet
      ? buildEndpointWithQuery(coaLedgerEndpoints.natures(), { includeSystemSet: true })
      : coaLedgerEndpoints.natures();
    const response = await get<ApiResponse<LedgerNatureDto[]>>(url);
    return response.data.data ?? [];
  }

  /** ADR-0027 ③c: BU ids the ledger is restricted to (empty = available to all branches). */
  async getBuAccess(id: ID): Promise<string[]> {
    const response = await get<ApiResponse<string[]>>(coaLedgerEndpoints.buAccess(id));
    return response.data.data ?? [];
  }

  /** ADR-0027 ③c: wholesale-replace the whitelist. Empty list = available to all branches. */
  async setBuAccess(id: ID, businessUnitIds: string[]): Promise<string[]> {
    const response = await put<ApiResponse<string[]>>(coaLedgerEndpoints.buAccess(id), businessUnitIds);
    return response.data.data ?? [];
  }

  async getById(id: ID): Promise<ApiResponse<CoaLedgerDto>> {
    const response = await get<ApiResponse<CoaLedgerDto>>(coaLedgerEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateCoaLedgerRequest): Promise<ApiResponse<CoaLedgerDto>> {
    const response = await post<ApiResponse<CoaLedgerDto>>(coaLedgerEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateCoaLedgerRequest): Promise<ApiResponse<CoaLedgerDto>> {
    const response = await put<ApiResponse<CoaLedgerDto>>(coaLedgerEndpoints.update(id), data);
    return response.data;
  }

  /** Soft-delete; rowVersion is required for optimistic-concurrency. */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(coaLedgerEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = coaLedgerEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }

  /** ADR-0061: next structured code for the subtype; null when it has no block or the block is full. */
  async getNextCode(accountSubtypeId: ID): Promise<string | null> {
    const url = buildEndpointWithQuery(coaLedgerEndpoints.nextCode(), { accountSubtypeId });
    const response = await get<ApiResponse<string | null>>(url);
    return response.data.data ?? null;
  }
}

export const coaLedgerService = new CoaLedgerService();
export default coaLedgerService;
